import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api/client'

interface User {
  id: string
  username: string
  email: string | null
  first_name: string | null
  last_name: string | null
  roles: string[]
}

interface TokenPayload {
  sub: string
  preferred_username: string
  email?: string
  given_name?: string
  family_name?: string
  azp?: string  // Authorized party (client ID)
  realm_access?: {
    roles: string[]
  }
  resource_access?: {
    [clientId: string]: {
      roles: string[]
    }
  }
  exp: number
}

const OIDC_CONFIG = {
  authority: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8180',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'master',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'blackandwhite',
  redirectUri: window.location.origin + '/callback',
  scope: 'openid profile email',
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

function parseJwt(token: string): TokenPayload | null {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return null
  }
}

function normalizeRole(role: string): string {
  return role.trim().toLowerCase()
}

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(localStorage.getItem('access_token'))
  const refreshToken = ref<string | null>(localStorage.getItem('refresh_token'))
  const user = ref<User | null>(null)
  const loading = ref(false)

  const isAuthenticated = computed(() => {
    if (!accessToken.value) return false
    const payload = parseJwt(accessToken.value)
    if (!payload) return false
    return payload.exp * 1000 > Date.now()
  })

  const normalizedRoles = computed(() => {
    return new Set(user.value?.roles.map(normalizeRole) ?? [])
  })

  const isAdmin = computed(() => {
    return normalizedRoles.value.has('admin') || normalizedRoles.value.has('blackandwhite_admin')
  })

  const hasAccess = computed(() => {
    return isAdmin.value ||
      normalizedRoles.value.has('access') ||
      normalizedRoles.value.has('user') ||
      normalizedRoles.value.has('blackandwhite_access')
  })

  function initUser() {
    if (accessToken.value) {
      const payload = parseJwt(accessToken.value)
      if (payload) {
        // Prefer client roles, but also accept legacy realm roles used by older setup docs.
        const clientRoles = payload.resource_access?.[OIDC_CONFIG.clientId]?.roles || []
        const realmRoles = payload.realm_access?.roles || []
        user.value = {
          id: payload.sub,
          username: payload.preferred_username,
          email: payload.email || null,
          first_name: payload.given_name || null,
          last_name: payload.family_name || null,
          roles: [...new Set([...clientRoles, ...realmRoles])],
        }
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken.value}`
      }
    }
  }

  async function login() {
    const codeVerifier = generateCodeVerifier()
    const codeChallenge = await generateCodeChallenge(codeVerifier)

    sessionStorage.setItem('code_verifier', codeVerifier)

    const authUrl = new URL(
      `${OIDC_CONFIG.authority}/realms/${OIDC_CONFIG.realm}/protocol/openid-connect/auth`
    )
    authUrl.searchParams.set('client_id', OIDC_CONFIG.clientId)
    authUrl.searchParams.set('redirect_uri', OIDC_CONFIG.redirectUri)
    authUrl.searchParams.set('response_type', 'code')
    authUrl.searchParams.set('scope', OIDC_CONFIG.scope)
    authUrl.searchParams.set('code_challenge', codeChallenge)
    authUrl.searchParams.set('code_challenge_method', 'S256')

    window.location.href = authUrl.toString()
  }

  async function handleCallback(code: string): Promise<boolean> {
    const codeVerifier = sessionStorage.getItem('code_verifier')
    if (!codeVerifier) {
      return false
    }

    sessionStorage.removeItem('code_verifier')

    try {
      const tokenUrl = `${OIDC_CONFIG.authority}/realms/${OIDC_CONFIG.realm}/protocol/openid-connect/token`

      const params = new URLSearchParams()
      params.set('grant_type', 'authorization_code')
      params.set('client_id', OIDC_CONFIG.clientId)
      params.set('code', code)
      params.set('redirect_uri', OIDC_CONFIG.redirectUri)
      params.set('code_verifier', codeVerifier)

      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params,
      })

      if (!response.ok) {
        return false
      }

      const data = await response.json()

      accessToken.value = data.access_token
      refreshToken.value = data.refresh_token

      localStorage.setItem('access_token', data.access_token)
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token)
      }

      initUser()

      return true
    } catch {
      return false
    }
  }

  async function refresh(): Promise<boolean> {
    if (!refreshToken.value) return false

    try {
      const tokenUrl = `${OIDC_CONFIG.authority}/realms/${OIDC_CONFIG.realm}/protocol/openid-connect/token`

      const params = new URLSearchParams()
      params.set('grant_type', 'refresh_token')
      params.set('client_id', OIDC_CONFIG.clientId)
      params.set('refresh_token', refreshToken.value)

      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params,
      })

      if (!response.ok) {
        logout()
        return false
      }

      const data = await response.json()

      accessToken.value = data.access_token
      refreshToken.value = data.refresh_token

      localStorage.setItem('access_token', data.access_token)
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token)
      }

      initUser()
      return true
    } catch {
      logout()
      return false
    }
  }

  function logout() {
    accessToken.value = null
    refreshToken.value = null
    user.value = null

    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')

    delete api.defaults.headers.common['Authorization']

    const logoutUrl = new URL(
      `${OIDC_CONFIG.authority}/realms/${OIDC_CONFIG.realm}/protocol/openid-connect/logout`
    )
    logoutUrl.searchParams.set('client_id', OIDC_CONFIG.clientId)
    logoutUrl.searchParams.set('post_logout_redirect_uri', window.location.origin)

    window.location.href = logoutUrl.toString()
  }

  // Initialize on store creation
  initUser()

  return {
    accessToken,
    user,
    loading,
    isAuthenticated,
    isAdmin,
    hasAccess,
    login,
    handleCallback,
    refresh,
    logout,
  }
})
